import { useQuery } from "@tanstack/react-query";
import { de } from "date-fns/locale/de";
import Holidays from "date-holidays";
import { useEffect, useMemo, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "../api/axios";
import { API_BASE_URL } from "../api/config";
import image from "../assets/images/hirsch 082_HoehereAufloesunguWeiterebeiThellmann.jpg";

registerLocale("de", de);

const hbHolidays = new Holidays("DE", "HB");

const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};
const parseInputDate = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split("-").map(Number);
    return new Date(year, month - 1, day);
};

const isWeekendDate = (date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
};

const isHolidayDate = (date) => {
    return Boolean(hbHolidays.isHoliday(date));
};

const isBlockedDate = (date) => {
    return isWeekendDate(date) || isHolidayDate(date);
};

const isBlockedInputDate = (dateString) => {
    const date = parseInputDate(dateString);
    return date ? isBlockedDate(date) : false;
};

const isBeforeDate = (leftDateString, rightDateString) => {
    return new Date(`${leftDateString}T00:00:00`) < new Date(`${rightDateString}T00:00:00`);
};

const getNextWeekday = (date) => {
    const nextDate = new Date(date);
    while (isBlockedDate(nextDate)) {
        nextDate.setDate(nextDate.getDate() + 1);
    }
    return nextDate;
};

const getPreviousWeekday = (date) => {
    const previousDate = new Date(date);
    while (isBlockedDate(previousDate)) {
        previousDate.setDate(previousDate.getDate() - 1);
    }
    return previousDate;
};

const getDateAfterBusinessDays = (date, businessDays) => {
    const targetDate = new Date(date);
    let remainingDays = businessDays;

    while (remainingDays > 0) {
        targetDate.setDate(targetDate.getDate() + 1);
        if (!isBlockedDate(targetDate)) {
            remainingDays -= 1;
        }
    }

    return targetDate;
};

const getMaxEndDate = (startDateString) => {
    if (!startDateString) return null;

    const start = parseInputDate(startDateString);
    if (!start) return null;

    const maxEndDate = new Date(start);
    maxEndDate.setDate(maxEndDate.getDate() + 7);
    return maxEndDate;
};

const normalizeWasteType = (value) => {
    return (value || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/&/g, " und ")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
};

const resolveWasteTypeIdFromSlug = (slug, wasteTypes) => {
    if (!slug || wasteTypes.length === 0) return "";

    const normalizedSlug = normalizeWasteType(slug);
    const normalizedTypes = wasteTypes.map((type) => ({
        ...type,
        normalizedName: normalizeWasteType(type.name),
    }));

    const exactMatch = normalizedTypes.find((type) => type.normalizedName === normalizedSlug);
    if (exactMatch) return exactMatch.id;

    const slugMatchers = {
        "baumisch-abfall": [/baumisch/, /bau-und-abbruch/],
        "bauschutt": [/bauschutt/],
        "gruenschnitt": [/grunschnitt|gruenschnitt/, /baum-und-strauchschnitt/],
        "holz-a4": [/holz-a4/, /holz-a-4/],
        "holz": [/^holz$/],
        "metall": [/altmetall|metall/],
        "papier-pappe": [/papier|pappe/],
        "boden-steine": [/boden|steine/],
        "altreifen": [/altreifen/],
    };

    const matchers = slugMatchers[normalizedSlug] || [];
    for (const matcher of matchers) {
        const fuzzyMatch = normalizedTypes.find((type) => matcher.test(type.normalizedName));
        if (fuzzyMatch) return fuzzyMatch.id;
    }

    return "";
};

const CONTAINER_TYPE_GROUPS = [
    { key: "big-bags", label: "Big Bags" },
    { key: "absetzcontainer", label: "Absetzcontainer" },
    { key: "abrollcontainer", label: "Abrollcontainer" },
];

const getContainerTypeKey = (containerName = "") => {
    const normalizedName = containerName.toLowerCase();

    if (normalizedName.includes("big bag")) {
        return "big-bags";
    }

    if (normalizedName.includes("absetzcontainer")) {
        return "absetzcontainer";
    }

    if (normalizedName.includes("abrollcontainer")) {
        return "abrollcontainer";
    }

    return null;
};

const sortContainersByCapacity = (leftContainer, rightContainer) =>
    (leftContainer.capacity || 0) - (rightContainer.capacity || 0);

const getGroupedContainers = (containers) => {
    const groupedContainers = containers.reduce((groups, container) => {
        const typeKey = getContainerTypeKey(container.name);

        if (!typeKey) {
            return groups;
        }

        if (!groups[typeKey]) {
            groups[typeKey] = [];
        }

        groups[typeKey].push(container);
        return groups;
    }, {});

    return CONTAINER_TYPE_GROUPS.map((group) => ({
        ...group,
        containers: (groupedContainers[group.key] || []).sort(sortContainersByCapacity),
    })).filter((group) => group.containers.length > 0);
};

const getOrderedContainers = (containers) =>
    getGroupedContainers(containers).flatMap((group) => group.containers);

const getBookingStorageValue = (key) =>
    sessionStorage.getItem(key) || localStorage.getItem(key) || "";

const setBookingStorageValue = (key, value) => {
    sessionStorage.setItem(key, value);
    localStorage.setItem(key, value);
};

const removeBookingStorageValue = (key) => {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
};

export default function ContainerBooking({ page, resetTrigger }) {
    const navigate = useNavigate();
    const location = useLocation();
    const minStartDate = getDateAfterBusinessDays(new Date(), 3);
    const [step, setStep] = useState(1);
    const storedBookingPlz = getBookingStorageValue("booking_plz");
    const storedPlacementType = getBookingStorageValue("booking_placement_type");
    const prefilledPlz = location.state?.preservedPlz || storedBookingPlz;
    const prefilledWasteTypeSlug = location.state?.prefilledWasteTypeSlug || getBookingStorageValue("booking_waste_type_slug");
    const prefilledPlacementType = location.state?.preservedPlacementType || storedPlacementType;
    const initialPlacementType = ["private", "public"].includes(prefilledPlacementType) ? prefilledPlacementType : null;
    const [plz, setPlz] = useState(prefilledPlz);
    const [knowsWasteType, setKnowsWasteType] = useState(prefilledWasteTypeSlug ? true : null);
    const [wasteType, setWasteType] = useState("");
    const [products, setProducts] = useState([]);
    const [selectedContainer, setSelectedContainer] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [cart, setCart] = useState(null);
    const [cartLoading, setCartLoading] = useState(true);
    const [pendingPlz, setPendingPlz] = useState("");
    const [showPlzConflict, setShowPlzConflict] = useState(false);
    const [clearingCart, setClearingCart] = useState(false);
    const [placementType, setPlacementType] = useState(initialPlacementType);

    const [startDate, setStartDate] = useState(() => formatDateForInput(getDateAfterBusinessDays(new Date(), 3)));
    const [endDate, setEndDate] = useState(() => {
        const initialStartDate = getDateAfterBusinessDays(new Date(), 3);
        const initialEndDate = new Date(initialStartDate);
        initialEndDate.setDate(initialEndDate.getDate() + 5);
        return formatDateForInput(getNextWeekday(initialEndDate));
    });
    const [quantity, setQuantity] = useState(1);

    const [productsLoaded, setProductsLoaded] = useState(false);

    // Price info state
    const [priceInfo, setPriceInfo] = useState(null);
    const [loadingPrice, setLoadingPrice] = useState(false);
    const cartPlz = cart?.deliveryZipCode || "";
    const cartHasItems = Boolean(cart?.items?.length);
    const lockedPlz = cartHasItems && /^\d{5}$/.test(cartPlz)
        ? cartPlz
        : (cartHasItems && /^\d{5}$/.test(storedBookingPlz) ? storedBookingPlz : "");
    const isValidPlz = /^\d{5}$/.test(plz);
    const needsPublicPlacementApproval =
        placementType === "public" && isValidPlz && (!plz.startsWith("28") || plz === "28195");
    const canAskWasteType = !showPlzConflict && isValidPlz && Boolean(placementType) && !needsPublicPlacementApproval;
    const groupedProducts = useMemo(() => getGroupedContainers(products), [products]);

    const fetchProducts = async () => {
        const response = await axios.get(`/containers/public/${wasteType}`);
        const data = await response.data;
        console.log("Fetched products data:", data);

        data.forEach((container, index) => {
            console.log(`Container ${index + 1} (${container.name}):`, {
                hasImages: !!container.images,
                imageCount: container.images?.length || 0,
                firstImageInfo: container.images?.[0] ? {
                    fileName: container.images[0].fileName,
                    hasBase64Data: !!container.images[0].base64Data,
                    base64Length: container.images[0].base64Data?.length || 0,
                    base64Preview: container.images[0].base64Data?.substring(0, 50) + '...'
                } : null
            });
        });

        setProducts(data);
        setProductsLoaded(true);

        const orderedContainers = getOrderedContainers(data);

        if (orderedContainers.length > 0) {
            setSelectedContainer(orderedContainers[0]);
            setCurrentImageIndex(0);
        } else {
            setSelectedContainer(null);
        }
    };

    const fetchWasteTypes = async () => {
        const response = await axios.get(`/wastetypes/public`);
        const data = await response.data;
        console.log("Fetched waste types data:", data);
        return data;
    };

    const fetchCart = async () => {
        setCartLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/cart`, {
                credentials: "include"
            });
            const data = await response.json();
            setCart(data);
        } catch (error) {
            console.error("Error fetching cart:", error);
            setCart(null);
        } finally {
            setCartLoading(false);
        }
    };

    const { data: wasteTypes = [] } = useQuery({
        queryKey: ["wastetypes"],
        queryFn: fetchWasteTypes,
    });

    useEffect(() => {
        fetchCart();
    }, []);

    useEffect(() => {
        if (!lockedPlz) return;

        setPlz(lockedPlz);
        setBookingStorageValue("booking_plz", lockedPlz);
        setPendingPlz("");
        setShowPlzConflict(false);
    }, [lockedPlz]);

    useEffect(() => {
        if (!prefilledWasteTypeSlug || wasteType || wasteTypes.length === 0) return;

        const resolvedId = resolveWasteTypeIdFromSlug(prefilledWasteTypeSlug, wasteTypes);
        if (resolvedId) {
            setWasteType(resolvedId);
            setKnowsWasteType(true);
        }
    }, [prefilledWasteTypeSlug, wasteType, wasteTypes]);

    useEffect(() => {
        if (!resetTrigger) return;

        const resetStartDateObj = getDateAfterBusinessDays(new Date(), 3);
        const resetStartDate = formatDateForInput(resetStartDateObj);
        const resetEndDateObj = new Date(resetStartDateObj);
        resetEndDateObj.setDate(resetEndDateObj.getDate() + 5);
        const resetEndDate = formatDateForInput(getNextWeekday(resetEndDateObj));

        setStep(1);
        setPlz("");
        setPendingPlz("");
        setShowPlzConflict(false);
        setPlacementType(null);
        setKnowsWasteType(null);
        setWasteType("");
        setProducts([]);
        setProductsLoaded(false);
        setSelectedContainer(null);
        setCurrentImageIndex(0);
        setPriceInfo(null);
        setQuantity(1);
        setStartDate(resetStartDate);
        setEndDate(resetEndDate);

        removeBookingStorageValue("booking_plz");
        removeBookingStorageValue("booking_waste_type_slug");
        removeBookingStorageValue("booking_placement_type");
    }, [resetTrigger]);

    useEffect(() => {
        if (!isValidPlz) {
            setPlacementType(null);
            setKnowsWasteType(null);
            setWasteType("");
            removeBookingStorageValue("booking_placement_type");
        }
    }, [isValidPlz]);

    useEffect(() => {
        if (lockedPlz) {
            setBookingStorageValue("booking_plz", lockedPlz);
            return;
        }

        if (isValidPlz) {
            setBookingStorageValue("booking_plz", plz);
            return;
        }

        removeBookingStorageValue("booking_plz");
        removeBookingStorageValue("booking_waste_type_slug");
        removeBookingStorageValue("booking_placement_type");
    }, [isValidPlz, lockedPlz, plz]);

    const clearCartItems = async () => {
        if (!cart?.items?.length) return;

        await Promise.all(
            cart.items.map((item) =>
                fetch(`${API_BASE_URL}/cart/item`, {
                    method: "DELETE",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ cartItemId: item.id }),
                    credentials: "include"
                })
            )
        );
    };

    const handlePlzChange = (nextValue) => {
        const sanitizedValue = nextValue.replace(/\D/g, "").slice(0, 5);

        if (!lockedPlz) {
            setPlz(sanitizedValue);
            return;
        }

        if (sanitizedValue === "" || lockedPlz.startsWith(sanitizedValue)) {
            setShowPlzConflict(false);
            setPendingPlz("");
            setPlz(sanitizedValue);
            return;
        }

        if (sanitizedValue === lockedPlz) {
            setShowPlzConflict(false);
            setPendingPlz("");
            setPlz(sanitizedValue);
            return;
        }

        setPendingPlz(sanitizedValue);
        setShowPlzConflict(true);
        setPlz(lockedPlz);
        setKnowsWasteType(null);
        setWasteType("");
    };

    const handleKeepCartPlz = () => {
        setPendingPlz("");
        setShowPlzConflict(false);
        setPlz(lockedPlz);
    };

    const handleOpenPlzChangeModal = () => {
        setPendingPlz("");
        setShowPlzConflict(true);
        setPlacementType(null);
        setKnowsWasteType(null);
        setWasteType("");
        removeBookingStorageValue("booking_placement_type");
    };

    const handleClearCartAndUseNewPlz = async () => {
        if (!/^\d{5}$/.test(pendingPlz)) {
            alert("Bitte geben Sie eine gültige 5-stellige Postleitzahl ein.");
            return;
        }

        setClearingCart(true);
        try {
            const response = await fetch(`${API_BASE_URL}/cart/reset`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ plz: pendingPlz }),
                credentials: "include"
            });

            if (!response.ok) {
                alert("Bitte geben Sie eine gültige Postleitzahl ein.");
                return
            }
            setCart((currentCart) => ({
                ...(currentCart || {}),
                items: [],
                deliveryZipCode: ""
            }));
            setPlz(pendingPlz);
            setBookingStorageValue("booking_plz", pendingPlz);
            removeBookingStorageValue("booking_waste_type_slug");
            setPendingPlz("");
            setShowPlzConflict(false);
            setPlacementType(null);
            setKnowsWasteType(null);
            setWasteType("");
            removeBookingStorageValue("booking_placement_type");
            await fetchCart();
        } catch (error) {
            console.error("Error clearing cart:", error);
            alert("Der Warenkorb konnte nicht geleert werden. Bitte versuchen Sie es erneut.");
        } finally {
            setClearingCart(false);
        }
    };

    // Fetch price info from API
    const fetchPriceInfo = async () => {
        if (!selectedContainer || !wasteType || !startDate || !endDate) {
            console.log("Missing required data for price fetch:", {
                selectedContainer: !!selectedContainer,
                wasteType: !!wasteType,
                startDate: !!startDate,
                endDate: !!endDate
            });
            return;
        }

        console.log("Fetching price info with:", {
            containerId: selectedContainer.id,
            wasteTypeId: wasteType,
            quantity: quantity,
            startDate: startDate,
            endDate: endDate
        });

        setLoadingPrice(true);
        try {
            const response = await fetch(`${API_BASE_URL}/containers/public/price`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    containerId: selectedContainer.id,
                    wasteTypeId: wasteType,
                    quantity: quantity,
                    startDate: startDate,
                    endDate: endDate
                }),
                credentials: "include"
            });

            if (response.ok) {
                const data = await response.json();
                console.log("Price info received:", data);
                setPriceInfo(data);
            } else {
                console.error("Error fetching price info:", response.status, await response.text());
                setPriceInfo(null);
            }
        } catch (error) {
            console.error("Error fetching price info:", error);
            setPriceInfo(null);
        } finally {
            setLoadingPrice(false);
        }
    };

    // Fetch price when relevant values change
    useEffect(() => {
        console.log("useEffect triggered with:", {
            step,
            selectedContainer: selectedContainer?.id,
            wasteType,
            startDate,
            endDate,
            quantity
        });

        if (step === 2 && selectedContainer && wasteType && startDate && endDate) {
            console.log("Calling fetchPriceInfo...");
            fetchPriceInfo();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedContainer, quantity, startDate, endDate, step, wasteType]);

    const handleNext = async () => {
        if (!isValidPlz) return alert("Bitte eine gültige 5-stellige Postleitzahl eingeben");
        if (!placementType) return alert("Bitte wählen Sie zuerst einen privaten oder öffentlichen Abstellplatz aus.");
        if (needsPublicPlacementApproval) {
            return alert("Für einen öffentlichen Stellplatz ist eine Genehmigung notwendig. Bitte melden Sie sich unter container@hirsch-gmbh.com.");
        }
        if (knowsWasteType !== true) return alert("Bitte wählen Sie zuerst aus, ob Sie Ihre Abfallart kennen.");
        if (!wasteType) return alert("Bitte Abfallart auswählen");

        const response = await fetch(`${API_BASE_URL}/cart`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ plz, wasteType }),
            credentials: "include",
        });

        if (!response.ok) {
            return alert("Bitte geben Sie eine gültige Postleitzahl ein.");
        }

        setBookingStorageValue("booking_plz", plz);
        setBookingStorageValue("booking_waste_type_slug", "");

        await fetchProducts();
        setStep(2);
    };

    const handleUnknownWasteType = () => {
        if (!isValidPlz) {
            alert("Bitte zuerst eine gültige 5-stellige Postleitzahl eingeben.");
            return;
        }

        setBookingStorageValue("booking_plz", plz);
        setBookingStorageValue("booking_waste_type_slug", "");
        setKnowsWasteType(false);

        const overviewElement = document.getElementById("abfallarten");
        if (overviewElement) {
            overviewElement.scrollIntoView({ behavior: "smooth" });
            return;
        }

        navigate("/", { state: { scrollToWasteTypes: true } });
    };

    const handlePlacementTypeChange = (nextPlacementType) => {
        setPlacementType(nextPlacementType);
        setBookingStorageValue("booking_placement_type", nextPlacementType);
        setKnowsWasteType(null);
        setWasteType("");
    };

    const toggleContainer = (id) => {
        setSelectedContainer(products.find((c) => c.id === id));
        setCurrentImageIndex(0);
    };

    const handleStartDateChange = (value) => {
        if (isBlockedInputDate(value)) {
            alert("Wochenenden und Feiertage können nicht als Bereitstellungsdatum ausgewählt werden.");
            return;
        }
        setStartDate(value);

        const maxEndDate = getMaxEndDate(value);
        const maxAllowedEndDate = maxEndDate ? formatDateForInput(getPreviousWeekday(maxEndDate)) : null;

        if (endDate && isBeforeDate(endDate, value)) {
            setEndDate(value);
            return;
        }

        if (maxAllowedEndDate && endDate && isBeforeDate(maxAllowedEndDate, endDate)) {
            setEndDate(maxAllowedEndDate);
        }
    };

    const handleEndDateChange = (value) => {
        if (isBlockedInputDate(value)) {
            alert("Wochenenden und Feiertage können nicht als Mietende ausgewählt werden.");
            return;
        }

        if (startDate && isBeforeDate(value, startDate)) {
            alert("Das Enddatum darf nicht vor dem Startdatum liegen.");
            return;
        }

        const maxEndDate = getMaxEndDate(startDate);
        if (maxEndDate && isBeforeDate(formatDateForInput(maxEndDate), value)) {
            alert("Das Enddatum darf maximal 7 Tage nach dem Startdatum liegen.");
            return;
        }

        setEndDate(value);
    };

    const handleAddToCart = async () => {
        if (!selectedContainer) return alert("Bitte einen Container auswählen");
        if (!startDate || !endDate) return alert("Bitte Mietzeitraum eingeben");
        if (isBeforeDate(endDate, startDate)) return alert("Das Enddatum darf nicht vor dem Startdatum liegen.");
        const maxEndDate = getMaxEndDate(startDate);
        if (maxEndDate && isBeforeDate(formatDateForInput(maxEndDate), endDate)) {
            return alert("Das Enddatum darf maximal 7 Tage nach dem Startdatum liegen.");
        }

        await fetch(`${API_BASE_URL}/cart/item`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                containerId: selectedContainer.id,
                quantity: quantity,
                startDate: startDate,
                endDate: endDate,
                wasteTypeId: wasteType
            }),
            credentials: "include"
        });

        navigate("/cart");
    };

    if (step === 2 && !selectedContainer) {
        if (productsLoaded && products.length === 0) {
            const wasteTypeName = wasteTypes.find((t) => t.id === wasteType)?.name || "diese Abfallart";
            return (
                <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                    <p className="text-gray-700 text-lg mb-2">
                        Für <span className="font-semibold">{wasteTypeName}</span> sind aktuell keine Container online buchbar.
                    </p>
                    <p className="text-gray-600">
                        Bitte schreiben Sie uns eine E-Mail an{" "}
                        <a href={`mailto:container@hirsch-gmbh.com?subject=Anfrage%20Container%20${encodeURIComponent(wasteTypeName)}`} className="text-pink-600 font-semibold hover:underline">
                            container@hirsch-gmbh.com
                        </a>
                        , wir helfen Ihnen gerne weiter.
                    </p>
                    <button
                        onClick={() => setStep(1)}
                        className="mt-6 px-6 py-3 rounded-lg bg-pink-600 text-white font-semibold hover:bg-pink-700 transition-colors"
                    >
                        Zurück zum Bestellfenster
                    </button>
                </div>
            );
        }
        return (
            <div className="flex justify-center items-center py-16">
                <svg className="animate-spin h-10 w-10 text-pink-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                </svg>
            </div>
        );
    }

    return (
        <div className="relative px-0 py-6 sm:px-6 sm:py-8 lg:px-10 lg:py-10" style={{ backgroundImage: "url('" + image + "')", backgroundSize: 'cover', backgroundPosition: 'center' }}>
            {showPlzConflict && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/60" onClick={clearingCart ? undefined : handleKeepCartPlz}></div>
                    <div className="relative w-full max-w-xl rounded-2xl bg-white p-6 sm:p-8 shadow-2xl">
                        <h3 className="text-xl sm:text-2xl font-bold text-amber-700 mb-3">
                            Andere Postleitzahl erkannt
                        </h3>
                        <p className="text-sm sm:text-base text-gray-700 mb-6 leading-relaxed">
                            Für Ihren Bestellvorgang ist die Postleitzahl <span className="font-bold">{lockedPlz}</span> hinterlegt. Wenn Sie mit einer anderen Postleitzahl weitermachen möchten, muss der Bestellvorgang neu gestartet werden. Falls sich in Ihrem Warenkorb bereits Artikel befinden, werden diese dabei entfernt.
                        </p>
                        <input
                            type="text"
                            value={pendingPlz}
                            onChange={(e) => setPendingPlz(e.target.value.replace(/\D/g, "").slice(0, 5))}
                            placeholder="Neue Postleitzahl"
                            inputMode="numeric"
                            maxLength={5}
                            disabled={clearingCart}
                            className="w-full p-4 rounded-lg border-2 border-gray-200 text-base outline-none focus:border-pink-600 transition-colors mb-6"
                        />
                        <div className="flex flex-col sm:flex-row gap-3">
                            <button
                                onClick={handleClearCartAndUseNewPlz}
                                disabled={clearingCart}
                                className="flex-1 px-4 py-3 rounded-lg bg-pink-600 text-white font-semibold hover:bg-pink-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {clearingCart ? "Bestellvorgang wird neu gestartet..." : "Bestellvorgang mit neuer Postleitzahl fortsetzen"}
                            </button>
                            <button
                                onClick={handleKeepCartPlz}
                                disabled={clearingCart}
                                className="flex-1 px-4 py-3 rounded-lg bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                Aktuelle Postleitzahl beibehalten
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Halbtransparenter schwarzer Overlay */}
            {step === 1 && <div className="absolute inset-0 bg-black opacity-60"></div>}

            {step === 1 && (
                <div className="max-w-7xl min-h-125 mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-between relative z-10" style={{ minHeight: '80vh' }}>
                    {/* Texte zentriert in der Mitte */}
                    <div className="flex-1 flex flex-col justify-center items-center text-center pt-8">
                        <h2 className="text-white text-5xl sm:text-6xl lg:text-7xl mb-4 sm:mb-6 font-bold">
                            ONLINESHOP
                        </h2>
                        <p className="text-white text-3xl sm:text-4xl lg:text-5xl font-bold">
                            FÜR ALLES, WAS IHNEN AUF DEN SACK GEHT
                        </p>
                        <p className="text-lg sm:text-xl lg:text-md text-white mb-4 lg:mb-6 mt-6">
                            Bei uns erhalten Sie immer den besten Preis - versprochen!
                        </p>
                        <p className="text-base sm:text-lg lg:text-md text-white max-w-5xl mx-auto  leading-relaxed">
                            Die Online-Bestellung funktioniert in nur drei Schritten: Postleitzahl eingeben, Stellplatz, Abfallart & Wunschtermin wählen und direkt bestellen - fertig!
                        </p>
                    </div>

                    {/* Input-Felder am unteren Rand */}
                    <div className="pb-12 sm:pb-16">
                        <div className="max-w-3xl mx-auto flex flex-col gap-4 sm:gap-5">
                            <input
                                type="text"
                                value={plz}
                                onChange={(e) => handlePlzChange(e.target.value)}
                                placeholder="Postleitzahl"
                                inputMode="numeric"
                                maxLength={5}
                                className={`w-full p-4 rounded-lg border-2 text-base outline-none transition-colors ${lockedPlz
                                    ? "border-gray-100 bg-gray-100 text-gray-500 cursor-not-allowed"
                                    : "border-gray-200 focus:border-pink-600"
                                    }`}
                                disabled={cartLoading || Boolean(lockedPlz)}
                                readOnly={Boolean(lockedPlz)}
                            />

                            {lockedPlz && !showPlzConflict && (
                                <div className="w-full p-4 rounded-lg bg-white/95 border border-pink-100 text-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                    <p>
                                        Für Ihren Bestellungsvorgang ist die Postleitzahl <span className="font-bold text-pink-600">{lockedPlz}</span> hinterlegt. Für weitere Artikel kann nur diese Postleitzahl verwendet werden.
                                    </p>
                                    <button
                                        onClick={handleOpenPlzChangeModal}
                                        className="text-pink-600 font-semibold hover:text-pink-700 transition-colors whitespace-nowrap"
                                    >
                                        Postleitzahl ändern
                                    </button>
                                </div>
                            )}

                            {!showPlzConflict && isValidPlz && (
                                <div className="w-full p-4 rounded-lg border-2 border-gray-200 bg-white text-left">
                                    <p className="text-sm sm:text-base font-semibold text-gray-800 mb-3">
                                        Welcher Abstellplatz wird verwendet?
                                    </p>
                                    <div className="flex gap-2 sm:gap-3">
                                        <button
                                            onClick={() => handlePlacementTypeChange("private")}
                                            className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-colors ${placementType === "private"
                                                ? "bg-pink-600 text-white"
                                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                                }`}
                                        >
                                            Privat
                                        </button>
                                        <button
                                            onClick={() => handlePlacementTypeChange("public")}
                                            className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-colors ${placementType === "public"
                                                ? "bg-pink-600 text-white"
                                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                                }`}
                                        >
                                            Öffentlich
                                        </button>
                                    </div>
                                </div>
                            )}

                            {!showPlzConflict && isValidPlz && needsPublicPlacementApproval && (
                                <div className="w-full p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 font-medium">
                                    Für einen öffentlichen Stellplatz ist eine Genehmigung notwendig. Bitte melden Sie sich unter <span className="font-bold">container@hirsch-gmbh.com</span> oder <span className="font-bold">0421 - 25802 - 0</span>.
                                </div>
                            )}

                            {canAskWasteType && (
                                <div className="w-full p-4 rounded-lg border-2 border-gray-200 bg-white text-left">
                                    <p className="text-sm sm:text-base font-semibold text-gray-800 mb-3">
                                        Kennen Sie Ihre Abfallart?
                                    </p>
                                    <div className="flex gap-2 sm:gap-3">
                                        <button
                                            onClick={() => setKnowsWasteType(true)}
                                            className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-colors ${knowsWasteType === true
                                                ? "bg-pink-600 text-white"
                                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                                }`}
                                        >
                                            Ja
                                        </button>
                                        <button
                                            onClick={handleUnknownWasteType}
                                            className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-colors ${knowsWasteType === false
                                                ? "bg-pink-600 text-white"
                                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                                }`}
                                        >
                                            Nein
                                        </button>
                                    </div>
                                </div>
                            )}

                            {canAskWasteType && knowsWasteType === true && (
                                <>
                                    <select
                                        value={wasteType}
                                        onChange={(e) => setWasteType(e.target.value)}
                                        className="w-full p-4 rounded-lg border-2 border-gray-200 text-base outline-none focus:border-pink-600 transition-colors appearance-none"
                                        style={{
                                            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M10.293 3.293L6 7.586 1.707 3.293A1 1 0 00.293 4.707l5 5a1 1 0 001.414 0l5-5a1 1 0 10-1.414-1.414z'/%3E%3C/svg%3E")`,
                                            backgroundRepeat: 'no-repeat',
                                            backgroundPosition: 'right 1rem center',
                                            backgroundSize: '1.2rem',
                                            paddingRight: '2.5rem'
                                        }}
                                    >
                                        <option value="">Abfallart auswählen</option>
                                        {wasteTypes.map((type) => (
                                            <option key={type.id} value={type.id}>
                                                {type.name}
                                            </option>
                                        ))}
                                    </select>

                                    <button
                                        onClick={handleNext}
                                        className="w-full px-8 py-4 rounded-lg border-none bg-pink-600 text-white font-semibold text-lg cursor-pointer hover:bg-pink-700 hover:shadow-lg transition-all duration-300"
                                    >
                                        Bestes Angebot finden
                                    </button>
                                </>
                            )}

                            {canAskWasteType && knowsWasteType === false && (
                                <div className="w-full p-4 rounded-lg bg-pink-50 border border-pink-100 text-pink-800 font-medium">
                                    Bitte wählen Sie unten in der Übersicht eine Abfallart aus. Wir übernehmen Ihre eingegebene Postleitzahl automatisch.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {step === 2 && (
                <div className="w-full max-w-7xl mx-auto min-h-screen bg-gray-50 px-3 py-6 sm:rounded-lg sm:px-6 sm:py-8 lg:px-10 lg:py-10">
                    {/* Header */}
                    <div className="pt-2 sm:pt-6 pb-4 sm:pb-5 px-1 sm:px-2 lg:px-4 text-center">
                        <h1 className="text-pink-600 text-2xl sm:text-3xl lg:text-4xl font-bold break-words">
                            {selectedContainer ? selectedContainer.name : 'Container auswählen'}
                        </h1>
                    </div>

                    {selectedContainer ? (
                        <div className="flex flex-col lg:flex-row w-full px-1 sm:px-2 lg:px-4 gap-6 lg:gap-8 items-start" style={{ maxWidth: 'none' }}>
                            {/* LINKS: Große Bildergalerie */}
                            <div className="w-full lg:flex-1" style={{ maxWidth: '100%', lgMaxWidth: '60%' }}>
                                {/* Hauptbild */}
                                <div className="mb-4 sm:mb-5">
                                    {selectedContainer.images && selectedContainer.images.length > 0 ? (
                                        <div className="relative">
                                            {(() => {
                                                const image = selectedContainer.images[currentImageIndex];
                                                let imageUrl = null;

                                                if (image.base64Data) {
                                                    const cleanBase64 = image.base64Data.replace(/\s/g, '');

                                                    if (cleanBase64.startsWith('data:')) {
                                                        imageUrl = cleanBase64;
                                                    } else {
                                                        let mimeType = 'image/jpeg';
                                                        if (image.fileName) {
                                                            const extension = image.fileName.split('.').pop()?.toLowerCase();
                                                            switch (extension) {
                                                                case 'webp': mimeType = 'image/webp'; break;
                                                                case 'png': mimeType = 'image/png'; break;
                                                                case 'jpg':
                                                                case 'jpeg': mimeType = 'image/jpeg'; break;
                                                                case 'gif': mimeType = 'image/gif'; break;
                                                                default: mimeType = 'image/jpeg'; break;
                                                            }
                                                        }
                                                        imageUrl = `data:${mimeType};base64,${cleanBase64}`;
                                                    }
                                                }

                                                return (
                                                    <div>
                                                        {imageUrl ? (
                                                            <img
                                                                src={imageUrl}
                                                                alt={`${selectedContainer.name}`}
                                                                className="w-full h-48 sm:h-64 md:h-80 lg:h-96 object-contain bg-white rounded-lg shadow-lg"
                                                                onError={(e) => {
                                                                    console.error('Fehler beim Laden des Bildes:', image.fileName);
                                                                    e.target.style.display = 'none';
                                                                    e.target.nextElementSibling.style.display = 'flex';
                                                                }}
                                                            />
                                                        ) : null}
                                                        <div className="w-full h-48 sm:h-64 md:h-80 lg:h-96 bg-white rounded-lg shadow-lg flex items-center justify-center"
                                                            style={{ display: imageUrl ? 'none' : 'flex' }}>
                                                            <span className="text-gray-400 text-base sm:text-lg">
                                                                Bild konnte nicht geladen werden
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    ) : (
                                        <div className="w-full h-48 sm:h-64 md:h-80 lg:h-96 bg-white rounded-lg shadow-lg flex items-center justify-center">
                                            <span className="text-gray-400 text-lg sm:text-xl">Kein Bild verfügbar</span>
                                        </div>
                                    )}
                                </div>

                                {/* Thumbnail Gallery */}
                                {selectedContainer.images && selectedContainer.images.length > 1 && (
                                    <div className="flex gap-2 sm:gap-2.5 flex-wrap mb-6 sm:mb-10">
                                        {selectedContainer.images.map((image, index) => {
                                            let thumbnailUrl = null;
                                            if (image.base64Data) {
                                                const cleanBase64 = image.base64Data.replace(/\s/g, '');
                                                if (cleanBase64.startsWith('data:')) {
                                                    thumbnailUrl = cleanBase64;
                                                } else {
                                                    let mimeType = 'image/jpeg';
                                                    if (image.fileName) {
                                                        const extension = image.fileName.split('.').pop()?.toLowerCase();
                                                        switch (extension) {
                                                            case 'webp': mimeType = 'image/webp'; break;
                                                            case 'png': mimeType = 'image/png'; break;
                                                            case 'jpg':
                                                            case 'jpeg': mimeType = 'image/jpeg'; break;
                                                            case 'gif': mimeType = 'image/gif'; break;
                                                            default: mimeType = 'image/jpeg'; break;
                                                        }
                                                    }
                                                    thumbnailUrl = `data:${mimeType};base64,${cleanBase64}`;
                                                }
                                            }

                                            return (
                                                <div
                                                    key={index}
                                                    onClick={() => setCurrentImageIndex(index)}
                                                    className={`w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden cursor-pointer bg-white ${currentImageIndex === index
                                                        ? 'border-2 sm:border-3 border-pink-500'
                                                        : 'border-2 border-gray-200'
                                                        }`}
                                                >
                                                    {thumbnailUrl ? (
                                                        <img
                                                            src={thumbnailUrl}
                                                            alt={`Thumbnail ${index + 1}`}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                                                            <span className="text-gray-400 text-xs">-</span>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Container Größenauswahl */}
                                <div className="mt-6 sm:mt-10">
                                    <div className="space-y-5 sm:space-y-6">
                                        {groupedProducts.map((group) => (
                                            <div key={group.key}>
                                                <h5 className="mb-3 text-base sm:text-lg font-bold text-gray-800">{group.label}</h5>
                                                <div className="flex flex-wrap gap-3 sm:gap-4 items-center">
                                                    {group.containers.map((container) => (
                                                        <div
                                                            key={container.id}
                                                            onClick={() => toggleContainer(container.id)}
                                                            className={`px-4 sm:px-5 py-3 sm:py-4 rounded-lg cursor-pointer text-center min-w-24 sm:min-w-30 transition-all duration-300 ${selectedContainer?.id === container.id
                                                                ? 'bg-pink-600 text-white'
                                                                : 'bg-gray-600 text-white hover:bg-pink-600'
                                                                }`}
                                                        >
                                                            <div className="text-base sm:text-lg font-bold">
                                                                {container.capacity}m³
                                                            </div>
                                                            <div className="text-base sm:text-lg font-bold">
                                                                € {container.price.toFixed(2)}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>


                            </div>

                            {/* RECHTS: Produktinfo und Buchung */}
                            <div className="w-full lg:w-auto lg:flex-shrink-0">
                                <div className="mb-6 sm:mb-8 p-4 sm:p-6 bg-white rounded-xl shadow-lg border border-gray-100">
                                    <h4 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">Bereitstellungszeitraum</h4>

                                    <div className="mb-4 sm:mb-5">

                                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                                            <div className="w-full sm:w-auto">
                                                {/* min date 3 days from today and exclude weekends */}
                                                <DatePicker
                                                    selected={parseInputDate(startDate)}
                                                    onChange={(date) => {
                                                        if (!date) return;
                                                        handleStartDateChange(formatDateForInput(date));
                                                    }}
                                                    locale="de"
                                                    minDate={minStartDate}
                                                    filterDate={(date) => !isBlockedDate(date)}
                                                    dateFormat="dd.MM.yyyy"
                                                    className="w-full p-2 sm:p-3 border-2 border-gray-200 rounded-lg text-sm sm:text-base font-medium bg-white focus:border-pink-500 outline-none transition-colors"
                                                />
                                            </div>
                                            <span className="text-gray-400 font-bold text-sm sm:text-base">bis</span>
                                            <div className="w-full sm:w-auto">
                                                <DatePicker
                                                    selected={parseInputDate(endDate)}
                                                    onChange={(date) => {
                                                        if (!date) return;
                                                        handleEndDateChange(formatDateForInput(date));
                                                    }}
                                                    locale="de"
                                                    minDate={parseInputDate(startDate) || minStartDate}
                                                    maxDate={getMaxEndDate(startDate) || undefined}
                                                    filterDate={(date) => !isBlockedDate(date)}
                                                    dateFormat="dd.MM.yyyy"
                                                    className="w-full p-2 sm:p-3 border-2 border-gray-200 rounded-lg text-sm sm:text-base font-medium bg-white focus:border-pink-500 outline-none transition-colors"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Quantity */}
                                <div className="mb-6 sm:mb-8 p-4 sm:p-6 bg-white rounded-xl shadow-lg border border-gray-100">
                                    <h4 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">Anzahl</h4>
                                    <input
                                        type="number"
                                        min="1"
                                        value={quantity}
                                        onChange={(e) => setQuantity(parseInt(e.target.value))}
                                        className="w-full p-2 sm:p-3 border-2 border-gray-200 rounded-lg text-sm sm:text-base font-medium bg-white focus:border-pink-500 outline-none transition-colors"
                                    />
                                </div>

                                {/* Preis und Produktinfo */}
                                <div className="text-center lg:text-right mb-6 sm:mb-8">
                                    <div className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2">
                                        {/* {selectedContainer.capacity}m³ {selectedContainer.name} */}
                                        {selectedContainer.name}
                                    </div>

                                    {/* Price breakdown */}
                                    {loadingPrice ? (
                                        <div className="flex justify-center lg:justify-end mb-4">
                                            <svg className="animate-spin h-8 w-8 text-pink-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                                            </svg>
                                        </div>
                                    ) : priceInfo ? (
                                        <div className="space-y-1 mb-4">
                                            {/* Total price */}
                                            <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black mb-2">
                                                € {(priceInfo.totalPrice / 100).toFixed(2)}
                                            </div>

                                            <div className="text-sm sm:text-base text-gray-600 text-center lg:text-right">
                                                {/* LIEFERKOSTEN read from env*/}
                                                Lieferkosten: {selectedContainer.requiresShipping ? (process.env.REACT_APP_SHIPPING_COSTS_DISPLAY / 100).toFixed(2) + " €" : "inklusive"}
                                                {/* Lieferkosten: {priceInfo.deliveryPrice === 0 ? "inklusive" : (priceInfo.deliveryPrice / 100).toFixed(2) + " €"} */}
                                            </div>
                                            {/* <div className="text-sm sm:text-base text-gray-600 text-center lg:text-right">
                                                Mietdauer: {(priceInfo.durationPrice / 100).toFixed(2)} €
                                            </div> */}
                                        </div>
                                    ) : (
                                        <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black mb-2">
                                            € {selectedContainer.price.toFixed(2)}
                                        </div>
                                    )}



                                    <div className="text-sm sm:text-base text-gray-600">
                                        Inkl. 19% MwSt. & Abholung
                                    </div>
                                </div>

                                {/* In den Warenkorb Button */}
                                <button
                                    onClick={handleAddToCart}
                                    className="w-full p-4 sm:p-5 bg-pink-600 text-white rounded-lg text-base sm:text-lg font-bold cursor-pointer hover:bg-pink-700 transition-all duration-300"
                                >
                                    In den Warenkorb
                                </button>

                                {/* Zurück Button */}
                                <button
                                    onClick={() => setStep(1)}
                                    className="w-full p-3 sm:p-4 bg-transparent text-gray-500 border-2 border-gray-200 rounded-lg text-sm sm:text-base font-semibold cursor-pointer mt-4 hover:border-gray-300 transition-colors"
                                >
                                    Zurück
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-12 sm:py-16 lg:py-25 px-4">
                            <h3 className="text-xl sm:text-2xl text-gray-600 mb-6 sm:mb-8">Wählen Sie einen Container aus:</h3>
                            <div className="space-y-6 sm:space-y-8">
                                {groupedProducts.map((group) => (
                                    <div key={group.key}>
                                        <h4 className="mb-4 text-lg sm:text-xl font-bold text-gray-800">{group.label}</h4>
                                        <div className="flex justify-center gap-4 sm:gap-5 flex-wrap">
                                            {group.containers.map((container) => (
                                                <div
                                                    key={container.id}
                                                    onClick={() => toggleContainer(container.id)}
                                                    className="bg-gray-600 text-white p-4 sm:p-5 rounded-lg cursor-pointer text-center min-w-32 sm:min-w-38 transition-all duration-300 hover:bg-pink-600"
                                                >
                                                    <div className="text-lg sm:text-xl font-bold">
                                                        {container.capacity}m³
                                                    </div>
                                                    <div className="text-base sm:text-lg font-bold">
                                                        € {container.price.toFixed(2)}
                                                    </div>
                                                    <div className="text-xs sm:text-sm opacity-90">
                                                        {(container.price / container.capacity).toFixed(2)}€
                                                    </div>
                                                    <div className="text-xs sm:text-sm mt-2">
                                                        {container.name}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {/* Technische Daten */}
                    <div className="mt-6 sm:mt-8 p-4 sm:p-6 bg-white rounded-xl shadow-lg border border-gray-100 mx-1 sm:mx-2 lg:mx-4">
                        <h4 className="text-lg sm:text-xl font-bold text-gray-800 mb-4 sm:mb-6">Technische Daten</h4>

                        <div className="grid gap-2 sm:gap-3">
                            <div className="flex justify-between items-center p-2 sm:p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <span className="font-semibold text-gray-700 text-sm sm:text-base">Volumen:</span>
                                <span className="font-bold text-pink-600 text-sm sm:text-base">{selectedContainer.capacity} m³</span>
                            </div>

                            <div className="flex justify-between items-center p-2 sm:p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <span className="font-semibold text-gray-700 text-sm sm:text-base">Länge:</span>
                                <span className="font-bold text-gray-800 text-sm sm:text-base">{selectedContainer.length} m</span>
                            </div>

                            <div className="flex justify-between items-center p-2 sm:p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <span className="font-semibold text-gray-700 text-sm sm:text-base">Breite:</span>
                                <span className="font-bold text-gray-800 text-sm sm:text-base">{selectedContainer.width} m</span>
                            </div>

                            <div className="flex justify-between items-center p-2 sm:p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <span className="font-semibold text-gray-700 text-sm sm:text-base">Höhe:</span>
                                <span className="font-bold text-gray-800 text-sm sm:text-base">{selectedContainer.height} m</span>
                            </div>

                            {/* <div className="flex justify-between items-center p-2 sm:p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <span className="font-semibold text-gray-700 text-sm sm:text-base">Gewicht:</span>
                                <span className="font-bold text-gray-800 text-sm sm:text-base">{selectedContainer.weight} kg</span>
                            </div> */}
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}